import { Button, Descriptions, Divider, Space, Typography, Spin, Table, Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import IndividualTraderService from '../../services/IndividualTraderService';
import { useAuth } from '../../hooks/useAuth';
import { getFileIconByURL } from '../../util/Common';
import { ArrowLeftOutlined } from '@ant-design/icons';
import CorporateTraderService from '../../services/CorporateTraderService';
import WorkflowAccModificationService from '../../services/WorkflowAccModificationService';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

export default function CorporateTraderDetail() {
  const { traderId } = useParams();
  const { showNotification } = useAuth();
  const [traderInformation, setTraderInformation] = useState();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const showModal = () => {
    setOpen(true);
  };

  const hideModal = () => {
    setOpen(false);
  };

  const formatValue = value => {
    // Handle boolean
    if (value === true) return 'YES';
    if (value === false) return 'NO';

    // Handle numbers (Potential Epoch Timestamps)
    if (typeof value === 'number') {
      const absValue = Math.abs(value);
      const len = absValue.toString().length;

      // 1. Check for Seconds (usually 9-10 digits)
      // Range: approx years 1900 to 2100
      if (len >= 9 && len <= 10) {
        const date = dayjs.unix(value);
        if (date.isValid()) return date.format('DD-MMMM-YYYY');
      }

      // 2. Check for Milliseconds (usually 12-13 digits)
      if (len >= 12 && len <= 13) {
        const date = dayjs(value);
        if (date.isValid()) return date.format('DD-MMMM-YYYY');
      }
    }

    return value;
  };

  const hanldeModifyTrader = async tradingAccountNo => {
    setConfirmLoading(true);
    const response = await WorkflowAccModificationService.prepareCorporateAcc({
      tradingAccountNo: tradingAccountNo,
    });
    setOpen(false);
    setConfirmLoading(false);
    if (response.success) {
      showNotification('success', 'Done! Successfully modified user status.');
      console.log(response);
      navigate('/modification/corporate/' + response.data.workFlowId);
    } else {
      showNotification('error', response.message);
    }
    console.log(response);
  };

  // Get individual trader with id
  const fetchData = async () => {
    // setLoading(true);
    try {
      const result = await CorporateTraderService.getCorporateTrader({
        traderId: traderId,
        // checkerId: user.userId,
      });
      console.log(result);
      // renderData(result.data);
      setTraderInformation(result.data);
    } catch (error) {
      showNotification('error', error.message);
    }
    // setLoading(false);
  };

  const covertCases = text => {
    const result = text.replace(/([A-Z])/g, ' $1');
    const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
    return finalResult;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const directorTable = [
    {
      title: 'ID',
      dataIndex: 'directorId',
      key: 'directorId',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'NRC',
      dataIndex: 'nrcNo',
      key: 'nrcNo',
    },
    {
      title: 'Resident Address',
      dataIndex: 'residentialAddress',
      key: 'residentialAddress',
    },
  ];

  const authPersonTable = [
    {
      title: 'ID',
      dataIndex: 'authorizedPersonId',
      key: 'authorizedPersonId',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'NRC',
      dataIndex: 'nrcNo',
      key: 'nrcNo',
    },
    {
      title: 'Designation',
      dataIndex: 'designation',
      key: 'designation',
    },
    {
      title: 'Signature',
      dataIndex: 'signature',
      key: 'signature',
      render: (signature, record) => {
        return <Text>{signature ? 'Yes' : 'No'}</Text>;
      },
    },
  ];

  const companyOwnerTable = [
    {
      title: 'ID',
      dataIndex: 'companyId',
      key: 'companyId',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'NRC',
      dataIndex: 'nrcNo',
      key: 'nrcNo',
    },
    {
      title: '% of Share Holders',
      dataIndex: 'percentOfShareholders',
      key: 'percentOfShareholders',
    },
  ];

  const goBack = () => {
    navigate(-1); // Go back one page in history
  };

  if (!traderInformation)
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          textAlign: 'center',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spin size="large" />
      </div>
    );
  return (
    <section>
      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Application Information
          </Title>
        }
        size={'middle'}
        column={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.applicationInfo).map(item => (
          <Descriptions.Item key={item} label={covertCases(item)}>
            <Text strong>{formatValue(traderInformation.applicationInfo[item])}</Text>
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider />
      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Corporate Trader
          </Title>
        }
        size={'middle'}
        column={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.corporateTrader).map(item => (
          <Descriptions.Item key={item} label={covertCases(item)}>
            <Text strong>{formatValue(traderInformation.corporateTrader[item])}</Text>
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider />
      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Email
          </Title>
        }
        size={'middle'}
        column={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        <Descriptions.Item label={'CC Email'}>
          <Text strong>{traderInformation.ccEmail}</Text>
        </Descriptions.Item>
        <Descriptions.Item label={'BCC Email'}>
          <Text strong>{traderInformation.bccEmail}</Text>
        </Descriptions.Item>
      </Descriptions>
      <Divider />
      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Trader Profile
          </Title>
        }
        size={'middle'}
        column={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.corporateProfile).map(item => (
          <Descriptions.Item key={item} label={covertCases(item)}>
            <Text strong>{formatValue(traderInformation.corporateProfile[item])}</Text>
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider />
      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Trading Bank Account
          </Title>
        }
        size={'middle'}
        column={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.tradingBankAccount).map(item => (
          <Descriptions.Item key={item} label={covertCases(item)}>
            <Text strong>{formatValue(traderInformation.tradingBankAccount[item])}</Text>
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider />
      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Corporate Bank Account
          </Title>
        }
        size={'middle'}
        column={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.corporateBankAccount).map(item => (
          <Descriptions.Item key={item} label={covertCases(item)}>
            <Text strong>{formatValue(traderInformation.corporateBankAccount[item])}</Text>
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider />
      {/* <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Communication Address
          </Title>
        }
        size={'middle'}
        column={{ xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.communicationAddress).map(item => (
          <Descriptions.Item key={item} label={covertCases(item)}>
            {traderInformation.communicationAddress[item] === true
              ? 'YES'
              : traderInformation.communicationAddress[item] === false
              ? 'NO'
              : traderInformation.communicationAddress[item]}
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider /> */}
      {/* <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Permanent Address
          </Title>
        }
        size={'middle'}
        column={{ xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.permanentAddress).map(item => (
          <Descriptions.Item key={item} label={covertCases(item)}>
            {traderInformation.permanentAddress[item] === true
              ? 'YES'
              : traderInformation.permanentAddress[item] === false
              ? 'NO'
              : traderInformation.permanentAddress[item]}
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider /> */}
      <Descriptions
        title={
          <Title level={5} style={{ marginTop: 0 }}>
            Corporate Proof Document
          </Title>
        }
        size={'middle'}
        column={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
      >
        {Object.keys(traderInformation.corporateProofDocument).map(item => (
          <Descriptions.Item key={item} label={covertCases(item).replace(/ Key$/, '')}>
            <a
              href={traderInformation.corporateProofDocument[item]}
              target="_blank"
              rel="noopener noreferrer"
            >
              {getFileIconByURL(traderInformation.corporateProofDocument[item])}{' '}
              {covertCases(item).replace(/ Key$/, '')}
            </a>
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider />
      <Title level={5} style={{ marginTop: 0, marginBottom: 28 }}>
        Company Director
      </Title>
      <Table
        columns={directorTable}
        dataSource={
          traderInformation !== null
            ? traderInformation.directorDataList.map((item, i) => ({
                ...item,
                key: i.toString(),
              }))
            : []
        }
        size={'large'}
        bordered
        style={{
          shadow: '0 0 5px solid black',
          marginBottom: '1rem',
        }}
        scroll={{
          x: 'max-content',
        }}
      />

      <Divider />
      <Title level={5} style={{ marginTop: 0, marginBottom: 28 }}>
        Authorized Person
      </Title>
      <Table
        columns={authPersonTable}
        dataSource={
          traderInformation !== null
            ? traderInformation.authorizedPersonDataList.map((item, i) => ({
                ...item,
                key: i.toString(),
              }))
            : []
        }
        size={'large'}
        bordered
        style={{
          shadow: '0 0 5px solid black',
          marginBottom: '1rem',
        }}
        scroll={{
          x: 'max-content',
        }}
      />

      <Divider />
      {/* <Descriptions
        title={ */}
      <Title level={5} style={{ marginTop: 0, marginBottom: 28 }}>
        Company Owner
      </Title>

      <Table
        columns={companyOwnerTable}
        dataSource={
          traderInformation !== null
            ? traderInformation.companyDataList.map((item, i) => ({
                ...item,
                key: i.toString(),
              }))
            : []
        }
        size={'large'}
        bordered
        style={{
          shadow: '0 0 5px solid black',
          marginBottom: '1rem',
        }}
        scroll={{
          x: 'max-content',
        }}
      />
      {/* </Descriptions.Item> */}
      {/* </Descriptions> */}
      <Divider />
      <Space
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItem: 'center',
          marginTop: '2rem',
        }}
      >
        <Button type="default" size={'large'} onClick={goBack}>
          <ArrowLeftOutlined /> Back
        </Button>
        <Button type="primary" danger size={'large'} onClick={showModal}>
          Modify
        </Button>
      </Space>
      {open && (
        <Modal
          title={traderInformation.corporateTrader.tradingAccountNo}
          open={open}
          onOk={() => hanldeModifyTrader(traderInformation.corporateTrader.tradingAccountNo)}
          onCancel={hideModal}
          okText="OK"
          cancelText="Cancel"
          confirmLoading={confirmLoading}
        >
          Are you sure to modify this trader?
        </Modal>
      )}
    </section>
  );
}
